import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { PromotionModel, PromotionTicketView } from 'src/app/model/promotion.model';
import { User } from 'src/app/model/user.model';
import { HelperService } from 'src/app/services/helper.service';

@Component({
  selector: 'app-promotions-carousel',
  templateUrl: './promotions-carousel.component.html',
  styleUrls: ['./promotions-carousel.component.scss'],
})
export class PromotionsCarouselComponent implements OnInit, OnChanges, OnDestroy {
  @Input() hideTitle: boolean = false;
  @Input() dataSource: PromotionTicketView[] = [];
  @Input() enrollInformation: User = null;
  @Output() exclusivePromotionRequested = new EventEmitter<PromotionTicketView>();

  currentPromos: PromotionTicketView[] = [];

  // Array de letras con su estado de color para la animación
  letters: { char: string; color: string }[] = [
    { char: 'R', color: '#FF0000' },
    { char: 'E', color: '#FF0000' },
    { char: 'C', color: '#FF0000' },
    { char: 'Á', color: '#FF0000' },
    { char: 'R', color: '#FF0000' },
    { char: 'G', color: '#FF0000' },
    { char: 'A', color: '#FF0000' },
    { char: 'T', color: '#FF0000' },
    { char: 'E', color: '#FF0000' },
  ];
  private animationSubscription: Subscription | undefined;
  private animationDirection: 'toGreen' | 'toRed' = 'toRed';
  private intervalTime: number = 100; // Tiempo entre pasos para animación más fluida

  constructor(private router: Router, readonly helperService: HelperService) {}

  ngOnInit() {
    this.currentPromos = this.dataSource;
    this.startAnimation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      this.currentPromos = changes['dataSource'].currentValue || [];
    }
  }

  ngOnDestroy() {
    if (this.animationSubscription) {
      this.animationSubscription.unsubscribe();
    }
  }

  goToPayment(source: PromotionModel): void {
    if (!source) {
      return;
    }

    localStorage.setItem('promotion-data', JSON.stringify(source));
    this.router.navigate([
      './payment-page',
      {
        card: localStorage.getItem('card-code'),
      },
    ]);
  }

  onExclusivePromotionClick(ticket: PromotionTicketView, event: MouseEvent): void {
    event?.stopPropagation();
    this.exclusivePromotionRequested.emit(ticket);
  }

  hasExclusivePromotion(ticket: PromotionTicketView): boolean {
    return !!ticket?.exclusivePromotion;
  }

  getAudienceLabel(promotion: PromotionModel): string {
    return promotion?.isEnabledAllCustomers ? 'RECARGA GENERAL' : 'RECARGA HAPPYLANDER';
  }

  getAudienceLabelShort(promotion: PromotionModel): string {
    return promotion?.isEnabledAllCustomers ? 'GENERAL' : 'HAPPYLANDER';
  }

  getPrimaryBenefitLabel(promotion: PromotionModel): string {
    switch (promotion?.typeOfPromotion) {
      case 'CASH_AND_TICKETS':
        return 'ETICKETS';
      case 'CASH_AND_GAMES':
        return 'JUGADAS';
      case 'ONLY_CASH':
      default:
        return 'BONUS';
    }
  }

  getPrimaryBenefitValue(promotion: PromotionModel): number {
    switch (promotion?.typeOfPromotion) {
      case 'CASH_AND_TICKETS':
        return promotion?.tickets ?? 0;
      case 'CASH_AND_GAMES':
        return (promotion?.token ?? 0) + (promotion?.bonusToken ?? 0);
      case 'ONLY_CASH':
      default:
        return promotion?.bonusAmount ?? 0;
    }
  }

  getTotalPlayable(promotion: PromotionModel): number {
    const amount = promotion?.amount ?? 0;
    const bonus = promotion?.bonusAmount ?? 0;
    return amount + bonus;
  }

  startAnimation() {
    let step = 0;
    const waveSteps = 30; // Pasos para que la ola recorra todas las letras
    const totalSteps = waveSteps * 2; // Completo: rojo->verde y verde->rojo

    this.animationSubscription = interval(this.intervalTime).subscribe(() => {
      step++;
      const currentStep = step % totalSteps;

      // Determinar si estamos en la fase rojo->verde o verde->rojo
      const isToGreen = currentStep < waveSteps;
      const phaseProgress = (currentStep % waveSteps) / waveSteps;

      // Aplicar colores a cada letra con efecto de ola
      this.letters.forEach((letter, index) => {
        // Calcular el progreso de cada letra basado en su posición
        // La ola se mueve de izquierda a derecha
        const letterPosition = index / (this.letters.length - 1); // 0 a 1

        // Calcular cuánto ha avanzado la ola para esta letra específica
        // Expandimos el rango para que la ola tenga un ancho visible
        const waveStart = phaseProgress - 0.1; // La ola empieza antes
        const waveEnd = phaseProgress + 0.1; // La ola termina después

        let letterProgress: number;

        if (letterPosition < waveStart) {
          // Esta letra ya fue tocada por la ola
          letterProgress = 1;
        } else if (letterPosition > waveEnd) {
          // Esta letra aún no es tocada por la ola
          letterProgress = 0;
        } else {
          // Esta letra está en medio de la ola (transición suave)
          letterProgress = (letterPosition - waveStart) / (waveEnd - waveStart);
        }

        // Interpolar color según la fase actual
        const color = this.interpolateColor(
          isToGreen ? '#FF0000' : '#008F39', // Color inicial
          isToGreen ? '#008F39' : '#FF0000', // Color final
          letterProgress
        );

        letter.color = color;
      });
    });
  }

  // Función auxiliar para interpolar entre dos colores
  private interpolateColor(color1: string, color2: string, progress: number): string {
    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);

    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * progress);
    const g = Math.round(g1 + (g2 - g1) * progress);
    const b = Math.round(b1 + (b2 - b1) * progress);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}
